export const INTENDED_USES = { unassigned: 'In inventory', randys_pc: "Randy’s PC", investment: 'Investment', shopify: 'Shopify', grandium: 'Grandium · earmarked', set_chase: 'Set Chase', auction: 'Auction Slab', whatnot_auction: 'Whatnot Auction', whatnot_chase: 'Whatnot Chase', whatnot_box: 'Whatnot Box' };
export const ACQUISITION_FIELDS = ['costGbp', 'purchasedOn', 'supplier', 'location', 'notes', 'batchId'];

// A batch total is not an individual slab cost. Only a separate, single-slab
// purchase can use its total when no allocated cost has been entered.
export function manualPurchasePolicy(row, isBatch = false) {
  const value = String(row.draft.costGbp || (!isBatch ? row.manualPurchase?.total || '' : '')).trim();
  const costGbp = /^(?:\d+\.?\d*|\.\d+)$/.test(value) ? Number(value) : null;
  const globalDisabled = Number.isFinite(costGbp) && costGbp > 500;
  const selected = row.manualPurchase?.vatScheme || 'UNKNOWN';
  return { costGbp, globalDisabled, vatScheme: globalDisabled && ['GLOBAL', 'UNKNOWN'].includes(selected) ? 'SECOND_HAND' : selected };
}

export function isBusinessSlab(slab) { return slab?.ownership === 'business'; }

export function canSetIntendedUse(row) {
  return isBusinessSlab(row.slab) && row.slab.handoff?.state !== 'recorded'
    && row.slab.state !== 'read_only_review' && !row.sending && !row.routing;
}

export function setSelectedIntendedUse(rows, intendedUse) {
  if (!Object.hasOwn(INTENDED_USES, intendedUse)) return rows;
  return rows.map(row => row.intake && row.selected && canSetIntendedUse(row)
    ? { ...row, intendedUseDraft: intendedUse, intendedUseTouched: true, notice: '' }
    : row);
}

function requireBusinessSlab(slab) {
  if (!isBusinessSlab(slab)) throw new Error('This cert belongs in your personal Slab Vault, not business inventory.');
}

export function parseCerts(value) {
  const tokens = String(value || '').split(/[\s,;]+/).map((part) => part.trim()).filter(Boolean);
  const seen = new Set();
  const certs = [];
  const invalid = [];
  let duplicates = 0;
  for (const token of tokens) {
    // Keep leading zeroes. A cert is an identifier, never a number.
    const cert = token.replace(/^#/, '').toUpperCase();
    if (!/^\d{5,12}$/.test(cert)) { invalid.push(token); continue; }
    if (seen.has(cert)) { duplicates += 1; continue; }
    seen.add(cert);
    certs.push(cert);
  }
  return { certs, invalid, duplicates };
}

export function createTaskQueue(concurrency = 4) {
  if (!Number.isInteger(concurrency) || concurrency < 1) throw new Error('Concurrency must be positive.');
  const waiting = [];
  let active = 0;
  function drain() {
    while (active < concurrency && waiting.length) {
      const task = waiting.shift();
      active += 1;
      Promise.resolve().then(task.run).then(task.resolve, task.reject).finally(() => { active -= 1; drain(); });
    }
  }
  return (run) => new Promise((resolve, reject) => { waiting.push({ run, resolve, reject }); drain(); });
}

export function blankSlab(certNumber) {
  return {
    id: null, certNumber, cardName: '', setName: '', grader: '', grade: '', cardNumber: null,
    images: { front: null, back: null }, ownership: 'business',
    acquisition: { costGbp: null, purchasedOn: null, supplier: null, location: null, notes: null, batchId: null },
    market: { valueGbp: null, status: 'unavailable', source: null, asOf: null, sourceUrl: null, reason: null },
    state: 'unsaved', updatedAt: null,
    actions: { shopify: { enabled: false, reason: 'Save this slab first.' }, grandium: { enabled: false, reason: 'Save this slab first.' } },
  };
}

export function acquisitionDraft(acquisition = {}) {
  return Object.fromEntries(ACQUISITION_FIELDS.map((key) => [key, acquisition[key] == null ? '' : String(acquisition[key])]));
}

export function createRow(slab, options = {}) {
  requireBusinessSlab(slab);
  const defaults = options.defaults || {};
  return {
    slab, intendedUseDraft: slab.intendedUse || 'unassigned', manualPurchase: null, handoffReference: slab.handoff?.reference || '', draft: acquisitionDraft(slab.id ? slab.acquisition : { ...slab.acquisition, ...defaults }),
    touched: {},
    intake: Boolean(options.intake), selected: Boolean(options.intake), revision: 0,
    lookup: slab.id ? 'ready' : 'idle', lookupError: '', comp: 'idle', compError: '', marketResolved: false,
    saving: false, saveError: '', saveUncertain: false, saveConflict: false, remoteMissing: false, sending: '', destinationUncertain: false, notice: '',
  };
}

export function isDirty(row) {
  if (row.slab.state === 'read_only_review') return false;
  if (!row.slab.id) return true;
  if (row.intendedUseDraft !== (row.slab.intendedUse || 'unassigned')) return true;
  const baseline = acquisitionDraft(row.slab.acquisition);
  return ACQUISITION_FIELDS.some((key) => {
    if (key === 'costGbp' && row.draft[key] !== '' && baseline[key] !== '') return Number(row.draft[key]) !== Number(baseline[key]);
    return row.draft[key] !== baseline[key];
  });
}

function newestMarket(row,slab){
 const incoming=slab.market,previous=row.slab.market;
 if(!row.marketResolved)return incoming;
 if(row.comp==='loading'||!incoming?.comps)return previous;
 const next=Date.parse(incoming.comps.checkedAt),prior=Date.parse(previous?.comps?.checkedAt);
 return !Number.isFinite(prior)||next>=prior?incoming:previous;
}
export function mergeRemoteRow(row, slab) {
  requireBusinessSlab(slab);
  if (row.slab.updatedAt && slab.updatedAt && Date.parse(slab.updatedAt) < Date.parse(row.slab.updatedAt)) return row;
  const incoming = acquisitionDraft(slab.acquisition);
  const draft = { ...row.draft };
  for (const key of ACQUISITION_FIELDS) {
    if (!row.touched[key] && (slab.id || !draft[key])) draft[key] = incoming[key];
  }
  return {
    ...row, intendedUseDraft: row.intendedUseTouched ? row.intendedUseDraft : slab.intendedUse || 'unassigned', slab: { ...slab, market: newestMarket(row,slab) }, draft,
  };
}

export function mergeSavedRow(row, slab, snapshot) {
  requireBusinessSlab(slab);
  const savedDraft = acquisitionDraft(slab.acquisition);
  const draft = { ...savedDraft };
  const touched = {};
  for (const key of ACQUISITION_FIELDS) {
    if (row.draft[key] !== snapshot.draft[key]) { draft[key] = row.draft[key]; touched[key] = true; }
  }
  return {
    ...row, intendedUseDraft: row.intendedUseDraft !== snapshot.intendedUse && snapshot.intendedUse !== undefined ? row.intendedUseDraft : slab.intendedUse || 'unassigned', intendedUseTouched: row.intendedUseDraft !== snapshot.intendedUse && snapshot.intendedUse !== undefined, manualPurchase: null, slab: { ...slab, market: row.marketResolved ? row.slab.market : slab.market }, draft, touched,
    saving: false, saveError: '', saveUncertain: false, saveConflict: false, remoteMissing: false, notice: Object.keys(touched).length ? 'Saved. New edits are still unsaved.' : 'Saved to business inventory.',
  };
}

export function acquisitionPayload(draft) {
  const cost = draft.costGbp.trim();
  if (cost && (!/^(?:\d+\.?\d*|\.\d+)$/.test(cost) || !Number.isFinite(Number(cost)) || Number(cost) < 0)) {
    throw new Error('Enter a valid purchase cost of £0 or more.');
  }
  if (cost && (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(cost) || Number(cost) > 99999999.99)) {
    throw new Error('Enter a purchase cost with no more than two decimal places, up to £99,999,999.99.');
  }
  const date = draft.purchasedOn.trim();
  if (date && (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(`${date}T12:00:00Z`)) || new Date(`${date}T12:00:00Z`).toISOString().slice(0, 10) !== date)) {
    throw new Error('Enter a valid purchase date.');
  }
  return Object.fromEntries(ACQUISITION_FIELDS.map((key) => [key, key === 'costGbp' ? (cost ? Number(cost) : null) : (draft[key].trim() || null)]));
}

export function destinationReason(row, destination) {
  if (!isBusinessSlab(row.slab)) return 'Personal slabs cannot be sent from business inventory.';
  if (row.remoteMissing) return 'This slab was not returned by inventory. Check its saved state before sending it anywhere.';
  if (row.destinationUncertain) return 'Previous request has an unknown outcome. Check the destination before trying again.';
  if (row.sending) return 'A destination request is in progress.';
  if (!row.slab.id) return 'Save this slab first.';
  if (!row.slab.updatedAt) return 'Refresh this saved slab before sending it to a destination.';
  if (isDirty(row) || row.saving || row.saveUncertain || row.saveConflict) return 'Save or check your acquisition changes first.';
  const action = row.slab.actions?.[destination];
  return action?.enabled === true ? '' : (action?.reason || 'This destination is not available yet.');
}

export function money(value) {
  return typeof value === 'number' && Number.isFinite(value) ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: value % 1 === 0 ? 0 : 2 }).format(value) : '—';
}

export function safeUrl(value, allowRelative = false) {
  if (typeof value !== 'string') return '';
  if (allowRelative && value.startsWith('/') && !value.startsWith('//')) return value;
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : ''; } catch { return ''; }
}

export function shortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function requestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
